import { useEffect, useRef, useState } from 'react'
import { useBeforeUnload, useBlocker, useNavigate } from 'react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  type NewsArticle,
  Mtds,
  toSaveErrorMessage,
  useAuth
} from 'prove-common'
import { AddNewsImage } from '../add-news-image/AddNewsImage'
import { NewsMarkdownField } from './NewsMarkdownField'
import {
  NewsArticlePreview,
  appOptions,
  typeOptions
} from './NewsArticlePreview'
import { newsDateInput, newsTimestamp } from '../../utils/newsDates'
import { saveNewsArticle } from '../../utils/newsApi'
import '../news/news.css'

const CHAR_LIMIT = 1500

type AddNewNewsArticleProps = {
  article?: NewsArticle
  returnTo?: string
  onSuccess: () => void
}

export const AddNewNewsArticle = ({
  article: initialArticle,
  returnTo = '/news',
  onSuccess
}: AddNewNewsArticleProps) => {
  const { authFetch, isAdmin } = useAuth()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [article] = useState(initialArticle)
  const [initial] = useState(() => ({
    subject: article?.subject ?? '',
    app: article?.app ?? 'ProveTa',
    icon: article?.icon ?? 'merge',
    date: article ? newsDateInput(article.date) : '',
    endDate: article ? newsDateInput(article.endDate) : ''
  }))
  const [draft, setDraft] = useState(initial)
  const [image, setImage] = useState<File>()
  const [imagePreview, setImagePreview] = useState<string>()
  const [showPreview, setShowPreview] = useState(false)
  const [savedArticle, setSavedArticle] = useState<NewsArticle>()
  const [errors, setErrors] = useState<Record<string, string>>({})
  const summaryRef = useRef<HTMLHeadingElement>(null)
  const leaveDialogRef = useRef<HTMLDialogElement>(null)
  const savingRef = useRef(false)
  const completedRef = useRef(false)
  const markdownPendingRef = useRef(false)
  const [markdownPending, setMarkdownPending] = useState(false)
  const dirty =
    JSON.stringify(draft) !== JSON.stringify(initial) ||
    !!image ||
    !!savedArticle
  const blocker = useBlocker(
    () =>
      !completedRef.current &&
      (dirty || markdownPendingRef.current || savingRef.current)
  )

  useEffect(() => {
    if (imagePreview) return () => URL.revokeObjectURL(imagePreview)
  }, [imagePreview])

  useBeforeUnload((event) => {
    if (
      !completedRef.current &&
      (dirty || markdownPendingRef.current || savingRef.current)
    ) {
      event.preventDefault()
      event.returnValue = ''
    }
  })

  useEffect(() => {
    if (blocker.state === 'blocked') leaveDialogRef.current?.showModal()
    else if (leaveDialogRef.current?.open) leaveDialogRef.current.close()
  }, [blocker.state])

  const invalidateNews = () => {
    void queryClient.invalidateQueries({ queryKey: ['news'] })
    void queryClient.invalidateQueries({ queryKey: ['newsLatest'] })
    void queryClient.invalidateQueries({ queryKey: ['newsArticle'] })
  }
  const save = useMutation({
    retry: false,
    mutationFn: async () => {
      if (!isAdmin)
        throw new Error('Du må være administrator for å lagre nyheter.')
      return saveNewsArticle({
        client: authFetch(),
        body: {
          subject: draft.subject,
          app: draft.app,
          icon: draft.icon,
          date: article
            ? newsTimestamp(draft.date, article.date)
            : new Date().toISOString(),
          endDate: newsTimestamp(draft.endDate, article?.endDate, true)
        },
        articleId: article?.id,
        savedArticle,
        image,
        onContentSaved: (saved) => {
          setSavedArticle(saved)
          invalidateNews()
        }
      })
    }
  })

  const focusErrors = (next: Record<string, string>) => {
    setErrors(next)
    setTimeout(() => summaryRef.current?.focus())
  }
  const handleSave = async () => {
    if (savingRef.current || markdownPendingRef.current) return
    const next: Record<string, string> = {}
    if (!draft.subject.trim())
      next['news-subject'] = 'Skriv innhold til artikkelen.'
    else if (draft.subject.replace(/\n+$/, '').length > CHAR_LIMIT)
      next['news-subject'] = `Innholdet kan ha maksimalt ${CHAR_LIMIT} tegn.`
    if (article) {
      try {
        newsTimestamp(draft.date, article.date)
      } catch {
        next['news-date'] = 'Velg en gyldig artikkeldato.'
      }
    }
    try {
      newsTimestamp(draft.endDate, article?.endDate, true)
    } catch {
      next['news-end-date'] = 'Velg en gyldig siste visningsdag.'
    }
    if (
      image &&
      (image.size === 0 ||
        (image.type ? image.type !== 'image/png' : !/\.png$/i.test(image.name)))
    )
      next['news-image'] = 'Velg en PNG-fil som ikke er tom.'
    if (Object.keys(next).length) {
      focusErrors(next)
      return
    }
    setErrors({})
    savingRef.current = true
    try {
      await save.mutateAsync()
      completedRef.current = true
      invalidateNews()
      if (blocker.state === 'blocked') blocker.reset()
      onSuccess()
    } catch (error) {
      focusErrors({
        'news-save': toSaveErrorMessage(error, 'lagre artikkelen')
      })
    } finally {
      savingRef.current = false
    }
  }
  const locked = save.isPending || !!savedArticle

  return (
    <>
      <Mtds.Grid data-center={showPreview ? 'xl' : 'md'} data-gap='6'>
        <Mtds.Alert data-color='info'>
          Lagring gjør artikkelen tilgjengelig med en gang dersom den ikke er
          utløpt. Nyeste artikkeldato vises først. Det finnes ikke kladder eller
          planlagt publisering.
        </Mtds.Alert>
        {savedArticle && (
          <Mtds.Alert data-color='warning'>
            Artikkel {savedArticle.id} er lagret og kan allerede vises. Bildet
            er ikke bekreftet lastet opp. Prøv bildeopplastingen igjen.
            Innholdet lagres ikke på nytt.
          </Mtds.Alert>
        )}
        <Mtds.Flex>
          <Mtds.Button
            type='button'
            data-variant='secondary'
            aria-expanded={showPreview}
            aria-controls='news-preview'
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview ? 'Skjul forhåndsvisning' : 'Vis forhåndsvisning'}
          </Mtds.Button>
        </Mtds.Flex>
        <Mtds.Grid
          data-items={showPreview ? '400' : undefined}
          data-gap='8'
          data-align='start'
        >
          <form
            noValidate
            onSubmit={(event) => {
              event.preventDefault()
              void handleSave()
            }}
          >
            <Mtds.Grid data-gap='6'>
              <Mtds.Errorsummary hidden={!Object.keys(errors).length}>
                <Mtds.Heading
                  as='h2'
                  data-size='sm'
                  tabIndex={-1}
                  ref={summaryRef}
                >
                  Artikkelen kunne ikke lagres ferdig
                </Mtds.Heading>
                <ul>
                  {Object.entries(errors).map(([id, message]) => (
                    <li key={id}>
                      <a
                        href={`#${id}`}
                        onClick={(event) => {
                          event.preventDefault()
                          const field = document.getElementById(id)
                          field?.scrollIntoView?.({ block: 'center' })
                          const editor = field?.querySelector<HTMLElement>(
                            '[contenteditable="true"]'
                          )
                          ;(editor ?? field)?.focus()
                        }}
                      >
                        {message}
                      </a>
                    </li>
                  ))}
                </ul>
              </Mtds.Errorsummary>
              <Mtds.Grid data-items='200' data-gap='4'>
                <Mtds.Field
                  as='select'
                  id='news-app'
                  label='App'
                  value={draft.app}
                  disabled={locked}
                  options={
                    appOptions.some((option) => option.value === draft.app)
                      ? appOptions
                      : [...appOptions, { value: draft.app, label: draft.app }]
                  }
                  onChange={(event) =>
                    setDraft({ ...draft, app: event.target.value })
                  }
                />
                <Mtds.Field
                  as='select'
                  id='news-type'
                  label='Type'
                  value={draft.icon}
                  disabled={locked}
                  options={
                    typeOptions.some((option) => option.value === draft.icon)
                      ? typeOptions
                      : [
                          ...typeOptions,
                          { value: draft.icon, label: draft.icon }
                        ]
                  }
                  onChange={(event) =>
                    setDraft({ ...draft, icon: event.target.value })
                  }
                />
              </Mtds.Grid>
              <NewsMarkdownField
                value={draft.subject}
                disabled={locked}
                charLimit={CHAR_LIMIT}
                error={errors['news-subject']}
                onChange={(subject) =>
                  setDraft((current) => ({ ...current, subject }))
                }
                onPendingChange={(pending) => {
                  markdownPendingRef.current = pending
                  setMarkdownPending(pending)
                }}
              />
              {article && (
                <Mtds.Field
                  as='input'
                  type='date'
                  id='news-date'
                  label='Artikkeldato'
                  required
                  description='Brukes til sortering, ikke til å utsette publisering. Datoen vises i norsk tid.'
                  value={draft.date}
                  disabled={locked}
                  validation={errors['news-date']}
                  aria-invalid={!!errors['news-date']}
                  onChange={(event) =>
                    setDraft({ ...draft, date: event.target.value })
                  }
                />
              )}
              <Mtds.Field
                as='input'
                type='date'
                id='news-end-date'
                label='Siste visningsdag'
                required
                description='Ved endring vises artikkelen ut hele den valgte dagen i Europe/Oslo. Eksisterende klokkeslett beholdes hvis datoen ikke endres.'
                value={draft.endDate}
                disabled={locked}
                validation={errors['news-end-date']}
                aria-invalid={!!errors['news-end-date']}
                onChange={(event) =>
                  setDraft({ ...draft, endDate: event.target.value })
                }
              />
              <AddNewsImage
                image={image}
                disabled={save.isPending}
                error={errors['news-image']}
                onChange={(file) => {
                  setImage(file)
                  setImagePreview(file ? URL.createObjectURL(file) : undefined)
                }}
              />
              {errors['news-save'] && (
                <Mtds.Alert as='div' data-color='danger' role='alert'>
                  {errors['news-save']}
                </Mtds.Alert>
              )}
              <Mtds.Flex>
                <Mtds.Button
                  id='news-save'
                  type='submit'
                  data-variant='primary'
                  disabled={save.isPending || markdownPending || !isAdmin}
                >
                  {save.isPending
                    ? 'Lagrer …'
                    : savedArticle
                      ? image
                        ? 'Prøv bildeopplasting igjen'
                        : 'Fullfør uten nytt bilde'
                      : 'Lagre artikkel'}
                </Mtds.Button>
                <Mtds.Button
                  type='button'
                  disabled={save.isPending}
                  onClick={() => void navigate(returnTo)}
                >
                  Avbryt
                </Mtds.Button>
              </Mtds.Flex>
              {markdownPending && (
                <Mtds.Muted role='status'>
                  Behandler tekstendringer …
                </Mtds.Muted>
              )}
            </Mtds.Grid>
          </form>
          {showPreview && (
            <NewsArticlePreview
              app={draft.app}
              icon={draft.icon}
              subject={draft.subject}
              image={image}
              imagePreview={imagePreview}
              articleId={article?.id}
            />
          )}
        </Mtds.Grid>
      </Mtds.Grid>
      <Mtds.Dialog
        ref={leaveDialogRef}
        aria-labelledby='news-leave-title'
        onCancel={(event) => {
          event.preventDefault()
          if (blocker.state === 'blocked') blocker.reset()
        }}
        onClose={() => {
          if (blocker.state === 'blocked') blocker.reset()
        }}
      >
        <Mtds.Prose>
          <Mtds.Heading as='h2' id='news-leave-title' data-size='sm'>
            Forlate redigeringen?
          </Mtds.Heading>
          <p>
            {savedArticle
              ? 'Artikkelen er allerede lagret. Bildet er ikke bekreftet lastet opp og det valgte bildet går tapt hvis du forlater siden.'
              : 'Du har ulagrede endringer. Endringene går tapt hvis du forlater siden.'}
          </p>
          {save.isPending && (
            <p>Lagring pågår. Vent til den er ferdig før du forlater siden.</p>
          )}
          <Mtds.Flex>
            <Mtds.Button
              type='button'
              data-variant='primary'
              autoFocus
              onClick={() => {
                if (blocker.state === 'blocked') blocker.reset()
              }}
            >
              Fortsett redigering
            </Mtds.Button>
            <Mtds.Button
              type='button'
              disabled={save.isPending}
              onClick={() => {
                if (blocker.state === 'blocked') blocker.proceed()
              }}
            >
              Forlat siden
            </Mtds.Button>
          </Mtds.Flex>
        </Mtds.Prose>
      </Mtds.Dialog>
    </>
  )
}
